import type { EntityType } from '../entity-types.js'
import { entityEvents } from './entity-events.js'
import type { EntityCommandSpec, EntityEvent, EventCommands } from './entity-events.js'
import { ruleSchema } from './rule.js'
import type { Rule, RuleAction } from './rule.js'

/**
 * Bridge between the browser-side focus vocabulary (`onEnter`/`onLeave`,
 * `EventCommands`) and the unified rule model (`entered`/`left`,
 * `automationConfigSchema.rules`). The UI and command runner keep speaking
 * `EventCommands`; only profile *storage* moves to `rules`, so focus config
 * appears in the same Automations index as lifecycle rules (ADR Decision 1).
 */

const eventToFocus: Record<EntityEvent, 'entered' | 'left'> = { onEnter: 'entered', onLeave: 'left' }
const focusToEvent: Record<'entered' | 'left', EntityEvent> = { entered: 'onEnter', left: 'onLeave' }

const entityEventNames = Object.keys(entityEvents) as EntityEvent[]

/** Deterministic id so a re-save replaces the same rule rather than accreting duplicates. */
const focusRuleId = (type: EntityType, event: EntityEvent): string => `focus:${type}:${event}`

const specToArgs = (spec: EntityCommandSpec): Record<string, unknown> => ({
  entityId: spec.entityId,
  ...(spec.commandParams !== undefined ? { commandParams: spec.commandParams } : {}),
  ...(spec.label !== undefined ? { label: spec.label } : {}),
})

const actionToSpec = (action: RuleAction): EntityCommandSpec => {
  const args = (action.args ?? {}) as { commandParams?: Record<string, unknown>; label?: string; entityId?: string }
  return {
    commandId: action.commandId,
    entityId: args.entityId ?? '',
    ...(args.commandParams !== undefined ? { commandParams: args.commandParams } : {}),
    ...(args.label !== undefined ? { label: args.label } : {}),
  }
}

const isFocusEvent = (event: string): event is 'entered' | 'left' => event === 'entered' || event === 'left'

export const focusEventCommandsToRules = (type: EntityType, eventCommands: EventCommands): Rule[] =>
  entityEventNames
    .filter(entityEvent => (eventCommands[entityEvent]?.length ?? 0) > 0)
    .map(entityEvent =>
      ruleSchema.parse({
        id: focusRuleId(type, entityEvent),
        event: eventToFocus[entityEvent],
        scope: 'self',
        conditions: [{ kind: 'type', entityType: type }],
        actions: (eventCommands[entityEvent] ?? []).map(spec => ({ commandId: spec.commandId, args: specToArgs(spec) })),
      })
    )

export const focusRulesToEventCommands = (type: EntityType, rules: Rule[]): EventCommands => {
  const result: EventCommands = {}
  for (const rule of rules) {
    if (rule.scope !== 'self' || !isFocusEvent(rule.event)) {
      continue
    }
    if (!rule.conditions.some(condition => condition.kind === 'type' && condition.entityType === type)) {
      continue
    }
    result[focusToEvent[rule.event]] = rule.actions.map(actionToSpec)
  }
  return result
}
